import { createMemo, createSignal, Switch, Match, For, Show, onMount } from "solid-js";
import { api, type StatusResponse, type ServerInfo, type LogEntry, type AdminControlPlaneSnapshot, type AdminAuditEntry } from "../shared";
import { getAdminRuntimeConfig } from "../runtime";

const ANSI_REGEX = /\x1b\[([\d;]+)m/g;

interface StyleState { fg: string; bg: string; bold: boolean; reset(): void }
function ansiState(): StyleState {
  return { fg: "", bg: "", bold: false, reset() { this.fg = ""; this.bg = ""; this.bold = false; } };
}
const ANSI_FG: Record<number, string> = {
  30: "#111", 31: "#e55", 32: "#5e5", 33: "#ee5", 34: "#55e", 35: "#e5e", 36: "#5ee", 37: "#ccc",
  90: "#555", 91: "#f88", 92: "#8f8", 93: "#ff8", 94: "#88f", 95: "#f8f", 96: "#8ff", 97: "#fff",
};
const ANSI_BG: Record<number, string> = {
  40: "#111", 41: "#e55", 42: "#5e5", 43: "#ee5", 44: "#55e", 45: "#e5e", 46: "#5ee", 47: "#ccc",
};

function ansiToHtml(text: string): string {
  const state = ansiState();
  const out: string[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  while ((match = ANSI_REGEX.exec(text)) !== null) {
    if (match.index > lastIdx) out.push(escapeHtml(text.slice(lastIdx, match.index)));
    const codes = match[1].split(";").map(Number);
    for (const code of codes) {
      if (code === 0) state.reset();
      else if (code === 1) state.bold = true;
      else if (ANSI_FG[code]) state.fg = ANSI_FG[code];
      else if (ANSI_BG[code]) state.bg = ANSI_BG[code];
    }
    const styles: string[] = [];
    if (state.fg) styles.push(`color:${state.fg}`);
    if (state.bg) styles.push(`background:${state.bg}`);
    if (state.bold) styles.push("font-weight:bold");
    lastIdx = match.index + match[0].length;
    if (styles.length > 0) out.push(`<span style="${styles.join(";")}">`);
  }
  if (lastIdx < text.length) out.push(escapeHtml(text.slice(lastIdx)));
  return out.join("");
}
function escapeHtml(s: string): string { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }

export function Dashboard(props: {
  status: StatusResponse | null;
  controlPlane: AdminControlPlaneSnapshot | null;
  onServerStart: (name: string, port?: number) => void;
  onServerStop: (name: string) => void;
  logs: Record<string, LogEntry[]>;
}) {
  const [loading, setLoading] = createSignal<Record<string, boolean>>({});
  const [error, setError] = createSignal("");
  const [ports, setPorts] = createSignal<Record<string, number>>({ api: 8787, web: 5173 });
  const [expandedLogs, setExpandedLogs] = createSignal<Record<string, boolean>>({});
  const [migrating, setMigrating] = createSignal(false);
  const [auditEntries, setAuditEntries] = createSignal<AdminAuditEntry[]>([]);
  const runtime = createMemo(() => getAdminRuntimeConfig());
  const supportsProcessControl = () => runtime().controlPlane.supportsProcessControl ?? true;
  const supportsDirectDb = () => runtime().controlPlane.supportsDirectDbAccess ?? true;

  onMount(() => {
    if (props.controlPlane?.controlPlane.supportsAuditLog) {
      api.controlPlane.audit(12).then((entries) => setAuditEntries(entries || [])).catch(() => {});
    }
  });

  async function runMigration() {
    setMigrating(true);
    try {
      await api.db.migrate();
      location.reload();
    } catch (e: any) { setError(e.message); }
    setMigrating(false);
  }

  async function handleAction(server: string, action: "start" | "stop") {
    setLoading((p) => ({ ...p, [server]: true }));
    try {
      if (action === "start") {
        await Promise.race([
          props.onServerStart(server, ports()[server]),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
        ]);
      } else {
        await props.onServerStop(server);
      }
    } catch (e: any) {
      setError(action === "start" ? "Server may still be starting..." : e.message);
    }
    setLoading((p) => ({ ...p, [server]: false }));
  }

  function toggleLogs(server: string) {
    setExpandedLogs((p) => ({ ...p, [server]: !p[server] }));
  }

  return (
    <div class="space-y-6">
      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">{error()}</div>
      </Show>

      <Show when={supportsDirectDb() && props.status && !props.status.db.migrationApplied}>
        <div class="bg-amber-900/30 border border-amber-700 text-amber-300 px-6 py-4 rounded-lg text-sm flex items-center justify-between">
          <div>
            <p class="font-medium">Database not initialized</p>
            <p class="text-amber-500/80 text-xs mt-1">Run the initial migration to create all tables.</p>
          </div>
          <button onClick={runMigration} disabled={migrating()} class="px-4 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-500 disabled:opacity-50 cursor-pointer">
            {migrating() ? "Running..." : "Run Initial Migration"}
          </button>
        </div>
      </Show>

      <Show when={supportsProcessControl()}>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ServerCard
            title="API Server"
            info={props.status?.api ?? null}
            loading={!!loading()["api"]}
            port={ports().api!}
            onPortChange={(p) => setPorts((prev) => ({ ...prev, api: p }))}
            onStart={() => handleAction("api", "start")}
            onStop={() => handleAction("api", "stop")}
            onToggleLogs={() => toggleLogs("api")}
            logsExpanded={!!expandedLogs()["api"]}
            logs={props.logs["api"] ?? []}
          />
          <ServerCard
            title="Web Server"
            info={props.status?.web ?? null}
            loading={!!loading()["web"]}
            port={ports().web!}
            onPortChange={(p) => setPorts((prev) => ({ ...prev, web: p }))}
            onStart={() => handleAction("web", "start")}
            onStop={() => handleAction("web", "stop")}
            onToggleLogs={() => toggleLogs("web")}
            logsExpanded={!!expandedLogs()["web"]}
            logs={props.logs["web"] ?? []}
          />
        </div>
      </Show>

      <Show when={props.status}>
        <div class="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 class="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">System</h2>
          <div class="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <MetaItem label="Environment" value={runtime().controlPlane.target} />
            <MetaItem label="Mode" value={runtime().controlPlane.mode} />
            <MetaItem label="DB Mode" value={props.status!.db.mode} />
            <MetaItem label="DB Path" value={props.status!.db.path} />
            <MetaItem label="Managed API" value={runtime().managedAppBaseUrl} />
            <MetaItem label="Managed Web" value={runtime().webAppBaseUrl ?? ""} />
            <MetaItem label="Uptime" value={formatUptime(props.status!.uptime)} />
          </div>
        </div>
      </Show>

      <Show when={runtime().controlPlane.supportsAuditLog}>
        <div class="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Recent Admin Audit</h2>
            <button
              onClick={() => api.controlPlane.audit(12).then(setAuditEntries).catch((e: any) => setError(e.message))}
              class="px-3 py-1.5 text-xs font-medium rounded border border-gray-700 text-gray-300 hover:bg-gray-800 cursor-pointer"
            >
              Refresh
            </button>
          </div>
          <Show
            when={auditEntries().length > 0}
            fallback={<div class="text-sm text-gray-500">No admin audit events yet.</div>}
          >
            <div class="space-y-2">
              <For each={auditEntries()}>
                {(entry) => (
                  <div class="rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm">
                    <div class="flex items-center justify-between gap-3">
                      <div class="text-gray-200 font-medium">{entry.action}</div>
                      <div class={`text-xs ${entry.success ? "text-emerald-400" : "text-red-400"}`}>
                        {entry.statusCode}
                      </div>
                    </div>
                    <div class="mt-1 text-xs text-gray-500">
                      {entry.actor} · {entry.resourceType}
                      <Show when={entry.resourceId}> · {entry.resourceId}</Show>
                      {" · "}
                      {entry.createdAt}
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function ServerCard(props: {
  title: string;
  info: ServerInfo | null;
  loading: boolean;
  port: number;
  onPortChange: (p: number) => void;
  onStart: () => void;
  onStop: () => void;
  onToggleLogs: () => void;
  logsExpanded: boolean;
  logs: LogEntry[];
}) {
  const running = () => props.info?.running ?? false;
  const managed = () => props.info?.managed ?? false;

  return (
    <div>
      <div class={`border rounded-lg p-6 transition-colors ${running() ? "border-emerald-700 bg-emerald-950/20 pulse-green" : "border-gray-800 bg-gray-900"}`}>
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <span class={`w-3 h-3 rounded-full ${running() ? "bg-emerald-400" : "bg-gray-600"}`} />
            <h3 class="text-lg font-semibold">{props.title}</h3>
          </div>
          <div class="flex items-center gap-2">
            <Show when={running() && !managed()}>
              <span class="text-xs px-2 py-0.5 bg-amber-900/40 text-amber-300 border border-amber-700 rounded">external</span>
            </Show>
            <span class={`text-sm font-medium ${running() ? "text-emerald-400" : "text-gray-500"}`}>{running() ? "Running" : "Stopped"}</span>
          </div>
        </div>

        <div class="space-y-2 text-sm text-gray-400 mb-4">
          <div><span class="text-gray-500">Port:</span> {props.info?.port ?? props.port}</div>
          <Show when={running() && props.info?.pid}>
            <div><span class="text-gray-500">PID:</span> {props.info?.pid}</div>
            <Show when={managed()}><div><span class="text-gray-500">Managed by:</span> Admin</div></Show>
          </Show>
        </div>

        <Switch>
          <Match when={running()}>
            <div class="flex gap-2">
              <a href={`http://localhost:${props.info?.port ?? props.port}`} target="_blank" rel="noopener" class="flex-1 px-4 py-2 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded-lg text-sm font-medium text-center hover:bg-cyan-900/50 transition-colors cursor-pointer no-underline">Open</a>
              <button onClick={props.onToggleLogs} class="px-4 py-2 bg-blue-900/30 border border-blue-700 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-900/50 transition-colors cursor-pointer">{props.logsExpanded ? "Hide Logs" : "Logs"}</button>
              <Show when={managed()}>
                <button onClick={props.onStop} disabled={props.loading} class="px-4 py-2 bg-red-900/30 border border-red-700 text-red-300 rounded-lg text-sm font-medium hover:bg-red-900/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer">Stop</button>
              </Show>
            </div>
          </Match>
          <Match when={!running()}>
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <input type="number" value={props.port} onInput={(e) => props.onPortChange(parseInt(e.currentTarget.value) || props.port)} class="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" />
                <span class="text-xs text-gray-500">port</span>
              </div>
              <div class="flex gap-2">
                <button onClick={props.onStart} disabled={props.loading} class="flex-1 px-4 py-2 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded-lg text-sm font-medium hover:bg-emerald-900/50 transition-colors disabled:opacity-50 cursor-pointer">Start</button>
                <button onClick={props.onToggleLogs} class="px-4 py-2 bg-blue-900/30 border border-blue-700 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-900/50 transition-colors cursor-pointer">{props.logsExpanded ? "Hide Logs" : "Logs"}</button>
              </div>
            </div>
          </Match>
        </Switch>
      </div>
      <Show when={props.logsExpanded}>
        <div class="border border-t-0 border-gray-700 rounded-b-lg bg-gray-950 max-h-64 overflow-auto font-mono text-xs">
          <div class="sticky top-0 bg-gray-900 px-4 py-2 border-b border-gray-800 text-gray-400 flex items-center justify-between">
            <span>{props.title} output</span>
            <button onClick={props.onToggleLogs} class="text-gray-500 hover:text-gray-300 cursor-pointer">✕</button>
          </div>
          <div class="p-3">
            <Show when={props.logs.length > 0} fallback={<div class="text-gray-600 text-center py-4">Waiting for output...</div>}>
              <For each={props.logs}>{(e) => <div class="whitespace-pre-wrap break-all leading-relaxed" innerHTML={ansiToHtml(e.text)} />}</For>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}

function MetaItem(props: { label: string; value: string }) {
  return <div><p class="text-gray-500 text-xs mb-1">{props.label}</p><p class="text-gray-200 font-medium truncate" title={props.value}>{props.value}</p></div>;
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  return `${h}h ${m}m ${s}s`;
}
